// The booked moment from Prism's onboarding-call v3 (its water.js): a drop lands on the check and the field ripples out

/** A soft packet of two or three long rings that leaves the check, travels slowly and dies with time and distance. */
const WAVE = { lambda: 120, speed: 280, front: 120, pack: 2.2, decayT: 1.6, decayD: 1100, end: 3.2, tail: 0.6 }
/** After the face has sprung to its size, so the two never share a frame. */
const DELAY_MS = 300

const smooth = (e0: number, e1: number, x: number) => {
  const u = Math.min(1, Math.max(0, (x - e0) / (e1 - e0)))
  return u * u * (3 - 2 * u)
}

/** The surface's height and slope at distance `d` and time `t`, in closed form so the shader and the floats agree. */
function waveAt(d: number, t: number) {
  const k = (Math.PI * 2) / WAVE.lambda
  const behind = WAVE.speed * t - d
  const lead = smooth(-WAVE.front / 2, WAVE.front / 2, behind)
  const pack = Math.exp(-((Math.max(0, behind) / (WAVE.lambda * WAVE.pack)) ** 2))
  const fade = 1 - smooth(WAVE.end - WAVE.tail, WAVE.end, t)
  const amp = lead * pack * Math.exp(-d / WAVE.decayD) * Math.exp(-t / WAVE.decayT) * fade
  const phase = -k * behind
  return { h: amp * Math.sin(phase), slope: amp * k * Math.cos(phase) }
}

const FRAGMENT = `
precision highp float;
uniform vec2 uRes; uniform float uDpr; uniform vec2 uO; uniform float uT;
uniform vec3 uSurf; uniform vec3 uWash; uniform vec3 uInfo; uniform float uTint;
const float PI = 3.14159265;
const float LAMBDA = ${WAVE.lambda.toFixed(1)}, SPEED = ${WAVE.speed.toFixed(1)}, FRONT = ${WAVE.front.toFixed(1)};
const float PACK = ${WAVE.pack.toFixed(2)}, DT = ${WAVE.decayT.toFixed(3)}, DD = ${WAVE.decayD.toFixed(1)};
const float TEND = ${WAVE.end.toFixed(2)}, TTAIL = ${WAVE.tail.toFixed(2)};
vec3 ground(vec2 q, float washK) {
  vec2 c = vec2(uRes.x * 0.5, 0.0);
  float s = length((q - c) / vec2(1.2 * uRes.x, 0.7 * uRes.y));
  float a = 0.7 * clamp(1.0 - s / 0.7, 0.0, 1.0) * uTint;
  return mix(mix(uSurf, uWash, a), uWash, washK);
}
void main() {
  vec2 p = vec2(gl_FragCoord.x, uRes.y * uDpr - gl_FragCoord.y) / uDpr;
  vec2 v = p - uO;
  float d = max(length(v), 1.0);
  vec2 dir = v / d;
  float k = 2.0 * PI / LAMBDA;
  float behind = SPEED * uT - d;
  float lead = smoothstep(-FRONT * 0.5, FRONT * 0.5, behind);
  float pk = max(0.0, behind) / (LAMBDA * PACK);
  float pack = exp(-pk * pk);
  float fade = 1.0 - smoothstep(TEND - TTAIL, TEND, uT);
  float amp = lead * pack * exp(-d / DD) * exp(-uT / DT) * fade;
  float ph = -k * behind;
  float h = amp * sin(ph);
  vec2 g = dir * amp * k * cos(ph);
  vec3 n = normalize(vec3(-g * 5.0, 1.0));
  float washK = 0.55 * lead * exp(-d / 320.0) * exp(-uT / 1.4) * fade;
  vec3 col = ground(p + n.xy * 24.0, washK);
  vec3 L = normalize(vec3(-0.45, -0.65, 0.62));
  float lit = dot(n, L) - L.z;
  col += lit > 0.0 ? lit * 0.30 * vec3(1.0) : lit * 0.07 * (vec3(1.0) - uInfo * 0.5);
  float spec = pow(max(dot(n, normalize(L + vec3(0.0, 0.0, 1.0))), 0.0), 90.0);
  col += spec * 0.26 * fade * lead;
  col = mix(col, uInfo, max(0.0, -h) * 0.06);
  gl_FragColor = vec4(clamp(col, 0.0, 1.0), 1.0);
}`

/** A CSS colour as the 0-1 RGB the shader wants, painted and read back so oklch resolves as the page draws it. */
function rgbOf(css: string): [number, number, number] {
  const canvas = document.createElement('canvas')
  canvas.width = canvas.height = 1
  const context = canvas.getContext('2d')
  if (!context) return [0, 0, 0]
  context.fillStyle = css
  context.fillRect(0, 0, 1, 1)
  const [r, g, b] = context.getImageData(0, 0, 1, 1).data
  return [r / 255, g / 255, b / 255]
}

type Water = {
  canvas: HTMLCanvasElement
  gl: WebGLRenderingContext
  uniforms: Record<string, WebGLUniformLocation | null>
}
// Compiled once and kept: compiling as the drop fell cost the ripple its first frames
let water: Water | null = null

function prepareWater(): Water | null {
  if (water) return water
  const canvas = document.createElement('canvas')
  const gl = canvas.getContext('webgl', { premultipliedAlpha: false, antialias: false })
  if (!gl) return null
  const shader = (type: number, source: string) => {
    const s = gl.createShader(type)
    if (!s) return null
    gl.shaderSource(s, source)
    gl.compileShader(s)
    return s
  }
  const vertex = shader(gl.VERTEX_SHADER, 'attribute vec2 a; void main(){ gl_Position = vec4(a, 0.0, 1.0); }')
  const fragment = shader(gl.FRAGMENT_SHADER, FRAGMENT)
  const program = gl.createProgram()
  if (!vertex || !fragment || !program) return null
  gl.attachShader(program, vertex)
  gl.attachShader(program, fragment)
  gl.linkProgram(program)
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return null
  gl.useProgram(program)
  gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer())
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW)
  const position = gl.getAttribLocation(program, 'a')
  gl.enableVertexAttribArray(position)
  gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0)
  const uniforms: Water['uniforms'] = {}
  for (const name of ['uRes', 'uDpr', 'uO', 'uT', 'uSurf', 'uWash', 'uInfo', 'uTint'])
    uniforms[name] = gl.getUniformLocation(program, name)
  water = { canvas, gl, uniforms }
  return water
}

export interface RippleColors {
  ground: string
  wash: string
  green: string
}

/** The center of `el` in the field's box, from layout offsets so a face still springing in can't throw it off. */
function centerIn(el: HTMLElement, root: HTMLElement, rootTop: number) {
  let x = 0
  let y = 0
  let node: HTMLElement | null = el
  while (node && node !== root) {
    x += node.offsetLeft
    y += node.offsetTop
    node = node.offsetParent as HTMLElement | null
  }
  return { x: x + el.offsetWidth / 2, y: y + el.offsetHeight / 2 - rootTop, w: el.offsetWidth }
}

/** Ripples `ground` (the field, laid out at its offsetTop in `root`) out from `drop`, floating `pieces` on it; returns a cancel. */
export function rippleWater(
  root: HTMLElement,
  ground: HTMLElement,
  drop: HTMLElement,
  pieces: Array<HTMLElement>,
  colors: RippleColors,
): () => void {
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) return () => {}
  const w = prepareWater()
  if (!w) return () => {}
  const { canvas, gl, uniforms } = w
  const width = ground.offsetWidth
  const height = ground.offsetHeight
  const dpr = Math.min(2, devicePixelRatio || 1)
  canvas.width = Math.round(width * dpr)
  canvas.height = Math.round(height * dpr)
  Object.assign(canvas.style, {
    position: 'absolute',
    inset: '0',
    width: '100%',
    height: '100%',
    opacity: '1',
    transition: 'opacity 300ms cubic-bezier(0.22, 1, 0.36, 1)',
  })

  const top = ground.offsetTop
  const origin = centerIn(drop, root, top)
  gl.viewport(0, 0, canvas.width, canvas.height)
  gl.uniform2f(uniforms.uRes, width, height)
  gl.uniform1f(uniforms.uDpr, dpr)
  gl.uniform2f(uniforms.uO, origin.x, origin.y)
  gl.uniform3fv(uniforms.uSurf, rgbOf(colors.ground))
  gl.uniform3fv(uniforms.uWash, rgbOf(colors.wash))
  gl.uniform3fv(uniforms.uInfo, rgbOf(colors.green))
  gl.uniform1f(uniforms.uT, 0)
  gl.uniform1f(uniforms.uTint, 0)
  gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4)

  const floats = [drop, ...pieces].map((el) => {
    const c = centerIn(el, root, top)
    const dx = c.x - origin.x
    const dy = c.y - origin.y
    const d = Math.hypot(dx, dy)
    return {
      el,
      x: dx,
      y: dy,
      d,
      ux: d ? dx / d : 0,
      uy: d ? dy / d : 0,
      span: Math.min(c.w / 2, 200),
      pose: [0, 0, 0, 0, 0],
    }
  })

  // Over the field's own gradient and under its grain
  ground.firstElementChild?.after(canvas)
  let frameId = 0
  let start = 0
  let last = 0
  const clear = () => {
    for (const f of floats) f.el.style.transform = ''
  }
  const frame = (now: number) => {
    if (!root.isConnected) return
    if (!start) start = last = now
    const t = (now - start) / 1000
    const dt = Math.min(0.05, (now - last) / 1000)
    last = now
    const tint = Math.min(1, Math.max(0, (t * 1000 + DELAY_MS - 300) / 700))
    gl.uniform1f(uniforms.uT, t)
    gl.uniform1f(uniforms.uTint, 1 - (1 - tint) ** 3)
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4)
    // A float has mass, so each follows the swell a beat behind rather than snapping to it
    const ease = 1 - Math.exp(-dt / 0.09)
    for (const f of floats) {
      let target: Array<number>
      if (f.d < 4) {
        const swell = t < 0.9 ? Math.sin((t / 0.9) * Math.PI * 2) * Math.exp(-t * 2.4) * 0.09 : 0
        target = [0, 0, 0, 0, swell]
      } else {
        const { h, slope } = waveAt(f.d, t)
        const left = waveAt(Math.hypot(f.x - f.span, f.y), t).h
        const right = waveAt(Math.hypot(f.x + f.span, f.y), t).h
        target = [-f.ux * slope * 7, -f.uy * slope * 7, h * 8, (right - left) * 3, 0]
      }
      for (let i = 0; i < 5; i++) f.pose[i] += (target[i] - f.pose[i]) * ease
      const [tx, ty, tz, roll, swell] = f.pose
      f.el.style.transform =
        f.d < 4
          ? `scale(${(1 + swell).toFixed(4)})`
          : `perspective(800px) translate3d(${tx.toFixed(2)}px, ${ty.toFixed(2)}px, ${tz.toFixed(2)}px) rotateY(${(-roll).toFixed(3)}deg)`
    }
    if (t < WAVE.end) {
      frameId = requestAnimationFrame(frame)
      return
    }
    clear()
    canvas.style.opacity = '0'
    window.setTimeout(() => canvas.remove(), 320)
  }
  const timeoutId = window.setTimeout(() => {
    if (root.isConnected) frameId = requestAnimationFrame(frame)
  }, DELAY_MS)

  return () => {
    window.clearTimeout(timeoutId)
    cancelAnimationFrame(frameId)
    clear()
    canvas.remove()
  }
}
